import React from "react";

import EcoIcon from "../eco-icon/EcoIcon";
import EcoSimbolo from "../../img/eco/simbolo.png";

import "./EcoPageHeader.css";

export type EcoCrumb = {
  label: string;
  /** Ausente no último item (a tela atual). */
  onClick?: () => void;
};

type EcoPageHeaderProps = {
  crumbs: EcoCrumb[];
  title: string;
  subtitle?: React.ReactNode;
  /** Botões à direita no desktop; no celular, abaixo da faixa verde. */
  actions?: React.ReactNode;
  /** Use o `useGoBack(fallback)` da tela — nunca `navigate` fixo. */
  onBack?: () => void;
  /** Rótulo do botão de voltar no desktop (no celular vira ícone com este `aria-label`). */
  backLabel?: string;
  /** Conteúdo à esquerda do título (ex.: a foto do colaborador na ficha). */
  leading?: React.ReactNode;
};

/**
 * Cabeçalho das telas internas do redesign. No desktop: trilha, título,
 * subtítulo e ações à direita. No celular (≤720px): faixa verde com o botão de
 * voltar e o título. A tela que o usa precisa estar dentro de um contêiner com
 * `--eco-page-gutter` (o recuo lateral), para a faixa encostar nas bordas.
 */
const EcoPageHeader: React.FC<EcoPageHeaderProps> = ({
  crumbs,
  title,
  subtitle,
  actions,
  onBack,
  backLabel = "Voltar",
  leading,
}) => (
  <header className="ecoPageHeader">
    <div className="ecoPageHeader__band">
      {onBack ? (
        <div className="ecoPageHeader__mobileBar">
          <button
            type="button"
            className="ecoPageHeader__iconBtn"
            onClick={onBack}
            aria-label={backLabel}
          >
            <EcoIcon name="arrowLeft" size={22} />
          </button>
          <img className="ecoPageHeader__symbol" src={EcoSimbolo} alt="" aria-hidden="true" />
          <span className="ecoPageHeader__barSpacer" aria-hidden="true" />
        </div>
      ) : null}

      <div className={`ecoPageHeader__main${leading ? " ecoPageHeader__main--leading" : ""}`}>
      {leading ? <div className="ecoPageHeader__leading">{leading}</div> : null}
      <div className="ecoPageHeader__text">
        <nav className="ecoPageHeader__crumbs" aria-label="Caminho">
          {crumbs.map((crumb, index) => (
            <React.Fragment key={`${crumb.label}-${index}`}>
              {index > 0 ? (
                <span className="ecoPageHeader__sep" aria-hidden="true">
                  /
                </span>
              ) : null}
              {crumb.onClick ? (
                <button type="button" className="ecoPageHeader__crumbLink" onClick={crumb.onClick}>
                  {crumb.label}
                </button>
              ) : (
                <span className="ecoPageHeader__crumbCurrent" aria-current="page">
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
        <h1 className="ecoPageHeader__title">{title}</h1>
        {subtitle ? <p className="ecoPageHeader__subtitle">{subtitle}</p> : null}
      </div>
      </div>
    </div>

    {actions || onBack ? (
      <div className={`ecoPageHeader__actions${actions ? "" : " ecoPageHeader__actions--backOnly"}`}>
        {actions}
        {onBack ? (
          <button type="button" className="ecoPageHeader__back" onClick={onBack}>
            <EcoIcon name="arrowLeft" size={18} />
            {backLabel}
          </button>
        ) : null}
      </div>
    ) : null}
  </header>
);

export default EcoPageHeader;
